"use client";

import React from "react";
import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { hasHTMLTags } from "@/helpers/has-html-tag";

const textVariants = cva("font-sans", {
  variants: {
    weight: {
      light: "font-light",
      regular: "font-normal",
      medium: "font-medium",
      semibold: "font-semibold",
      bold: "font-bold",
    },
    align: {
      left: "text-left",
      center: "text-center",
      right: "text-right",
      justify: "text-justify",
    },
    color: {
      default: "text-inherit",
      muted: "text-muted-foreground",
      primary: "text-primary",
      secondary: "text-secondary",
      accent: "text-accent",
      destructive: "text-destructive",
    },
    wrap: {
      wrap: "whitespace-normal",
      nowrap: "whitespace-nowrap",
      pretty: "text-pretty",
      balance: "text-balance",
    },
    truncate: {
      true: "truncate block",
      false: "",
    },
  },
  defaultVariants: {
    weight: "regular",
    color: "default",
    wrap: "wrap",
  },
});

export interface TextProps
  extends
    Omit<useRender.ComponentProps<"p">, "color" | "wrap">,
    VariantProps<typeof textVariants> {
  as?: "span" | "div" | "p" | "label";
  forceAs?: boolean;
}

function Text({
  className,
  as = "p",
  weight,
  align,
  color,
  wrap,
  truncate,
  render,
  children,
  forceAs,
  ...props
}: TextProps) {
  const isHtmlString = typeof children === "string" && hasHTMLTags(children);
  const defaultTagName = forceAs ? as : isHtmlString ? "div" : as;
  const contentProps = isHtmlString
    ? { dangerouslySetInnerHTML: { __html: children } }
    : { children };
  const internalRef = React.useRef<HTMLElement | null>(null);

  const defaultProps: useRender.ElementProps<"p"> = {
    className: cn(
      textVariants({
        weight,
        align,
        color,
        wrap,
        truncate,
      }),
      className,
    ),
    ...contentProps,
  };

  return useRender({
    defaultTagName: defaultTagName,
    ref: internalRef,
    props: mergeProps<"p">(defaultProps, props),
    render: render,
    state: {
      slot: "text",
      weight,
    },
  });
}

export { Text, textVariants };
